import {
  HIDDEN_ROLES,
  PER_SOURCE_CARDS,
  ResolvedRoleLayout,
  conversionFor,
  layoutFor,
  seriesColor,
} from '@core/constants/role-layout.constant';
import { zonedInstant } from '@core/helpers/slot-series.helper';
import {
  InstallationTelemetry,
  MeasurementPoint,
  Point,
  Source,
} from '@core/models/installation.model';

export interface InsightSeries {
  point: MeasurementPoint;
  telemetry: Point | null;
  layout: ResolvedRoleLayout;
  color: string;
}

export interface InsightCard {
  key: string;
  // A known card's title is a translation key; an unknown role's card is titled with the API's
  // display name.
  titleKey: string | null;
  title: string;
  // Set when a heat pump card is split per heat pump, to tell the cards apart.
  sourceLabel: string | null;
  sortOrder: number;
  series: InsightSeries[];
}

export function sourceKey(source: Source | null | undefined): string {
  const deviceId = source?.deviceId ?? null;
  if (deviceId === null) {
    return 'unbound';
  }
  return `${deviceId}:${source?.contextNumber ?? ''}`;
}

export function sourceGroup(point: MeasurementPoint): string {
  return sourceKey(point.binding?.source);
}

export function sourceLabel(point: MeasurementPoint): string | null {
  const source = point.binding?.source;
  if (!source) {
    return null;
  }
  const channel = source.label ?? source.contextNumber;
  return [source.ldn, channel].filter(part => part !== null && part !== undefined).join(' ');
}

function perSourceGroups(points: MeasurementPoint[]): Map<string, Set<string>> {
  const groupsByCard = new Map<string, Set<string>>();
  for (const point of points) {
    const layout = layoutFor(point.role, point.kind);
    if (layout.known && PER_SOURCE_CARDS.has(layout.card)) {
      const groups = groupsByCard.get(layout.card) ?? new Set<string>();
      groups.add(sourceGroup(point));
      groupsByCard.set(layout.card, groups);
    }
  }
  return groupsByCard;
}

function emptyCard(
  key: string,
  point: MeasurementPoint,
  layout: ResolvedRoleLayout,
  split: boolean,
): InsightCard {
  return {
    key,
    titleKey: layout.known ? `installations.insights.cards.${layout.card}` : null,
    title: layout.known ? '' : point.roleDisplayName,
    sourceLabel: split ? sourceLabel(point) : null,
    sortOrder: layout.sortOrder,
    series: [],
  };
}

// The cards are decided by the installation's active points, never by which slots arrived, so a
// bound point with no data still gets its card.
export function buildCardSet(points: MeasurementPoint[], telemetryPoints: Point[]): InsightCard[] {
  const telemetryById = new Map(telemetryPoints.map(point => [point.pointId, point]));
  const active = points.filter(point => point.state === 'active' && !HIDDEN_ROLES.has(point.role));

  const groupsByCard = perSourceGroups(active);

  const cards = new Map<string, InsightCard>();
  for (const point of active) {
    const layout = layoutFor(point.role, point.kind);
    const split = layout.known && (groupsByCard.get(layout.card)?.size ?? 0) > 1;
    const key = split ? `${layout.cardKey}:${sourceGroup(point)}` : layout.cardKey;

    let card = cards.get(key);
    if (!card) {
      card = emptyCard(key, point, layout, split);
      cards.set(key, card);
    }
    card.sortOrder = Math.min(card.sortOrder, layout.sortOrder);
    const occurrence = card.series.filter(series => series.point.role === point.role).length;
    card.series.push({
      point,
      telemetry: telemetryById.get(point.pointOid) ?? null,
      layout,
      color: seriesColor(layout.colorToken, occurrence),
    });
  }

  const ordered = [...cards.values()];
  for (const card of ordered) {
    card.series.sort(
      (a, b) => a.layout.sortOrder - b.layout.sortOrder || a.point.name.localeCompare(b.point.name),
    );
  }
  return ordered.sort((a, b) => a.sortOrder - b.sortOrder || a.key.localeCompare(b.key));
}

export function hasBarSeries(card: InsightCard): boolean {
  return card.series.some(series => series.layout.chart === 'bar');
}

export interface GridItem {
  key: string;
  // Null for the PV performance card, which is not built from points.
  card: InsightCard | null;
  // The card that spans the whole width of the grid.
  main: boolean;
}

const PERFORMANCE_KEY = 'performance';

// The preset names the card the installation is about; without one, or when that card is absent,
// the grid card leads, then the PV production card, then whatever sorts first.
const PRESET_MAIN_CARD: Readonly<
  Record<string, (cards: InsightCard[]) => InsightCard | undefined>
> = {
  grid_metering: cards => cards.find(card => card.key === 'grid'),
  pv_monitoring: cards => pvProductionCard(cards),
};

export function pvProductionCard(cards: InsightCard[]): InsightCard | undefined {
  return cards.find(card => card.series.some(series => series.point.role === 'pv_production'));
}

export function mainCard(
  cards: InsightCard[],
  presetCode: string | null | undefined,
): InsightCard | undefined {
  const preferred = presetCode ? PRESET_MAIN_CARD[presetCode]?.(cards) : undefined;
  return (
    preferred ?? cards.find(card => card.key === 'grid') ?? pvProductionCard(cards) ?? cards[0]
  );
}

// The main card first, the others in their own order, and the PV performance card directly after
// the card that holds PV production; with no such card it closes the grid.
export function gridItems(
  cards: InsightCard[],
  presetCode: string | null | undefined,
  withPerformance: boolean,
): GridItem[] {
  const main = mainCard(cards, presetCode);
  const pvCard = pvProductionCard(cards);
  const items: GridItem[] = [];
  const add = (card: InsightCard): void => {
    items.push({ key: card.key, card, main: card === main });
    if (withPerformance && card === pvCard) {
      items.push({ key: PERFORMANCE_KEY, card: null, main: false });
    }
  };
  if (main) {
    add(main);
  }
  for (const card of cards) {
    if (card !== main) {
      add(card);
    }
  }
  if (withPerformance && !pvCard) {
    items.push({ key: PERFORMANCE_KEY, card: null, main: false });
  }
  return items;
}

export function unknownRoles(points: MeasurementPoint[]): string[] {
  const roles = points
    .filter(point => point.state === 'active' && !layoutFor(point.role, point.kind).known)
    .map(point => point.role);
  return [...new Set(roles)];
}

export function displayValue(value: number | null, unit: string): number | null {
  if (value === null) {
    return null;
  }
  return value * conversionFor(unit).factor;
}

export function formatDisplayValue(value: number | null, unit: string): string {
  if (value === null) {
    return '–';
  }
  const { decimals } = conversionFor(unit);
  return (value * conversionFor(unit).factor).toFixed(decimals);
}

const SLOT_LABEL_FORMAT: Record<string, string> = {
  'quarter-hour': 'H:mm',
  hour: 'H:mm',
  week: 'ddd D',
  day: 'D',
  month: 'MMM',
};

// Every point shares the period's slot grid, so the first point's slot times are the axis.
export function slotTimes(telemetry: InstallationTelemetry | null): string[] {
  return telemetry?.points[0]?.slots.map(slot => slot.time) ?? [];
}

export function slotLabels(telemetry: InstallationTelemetry | null): string[] {
  if (!telemetry) {
    return [];
  }
  const format = SLOT_LABEL_FORMAT[telemetry.granularity] ?? 'YYYY-MM-DD HH:mm';
  return slotTimes(telemetry).map(time => zonedInstant(time, telemetry.timezone, format));
}
